
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { User } from "@/components/admin/types";

interface UseAdminUsersProps {
  search: string;
  currentPage: number;
  itemsPerPage: number;
}

interface UseAdminUsersReturn {
  users: User[];
  loading: boolean;
  totalPages: number;
  handleDeleteUser: (userId: string) => Promise<void>;
}

export const useAdminUsers = ({ search, currentPage, itemsPerPage }: UseAdminUsersProps): UseAdminUsersReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.ilike('username', `%${search}%`);
      }

      const { count } = await query;

      const { data: profiles, error } = await supabase
        .from('profiles')
        .select(`
          id,
          username,
          created_at,
          is_superuser,
          is_deleted,
          creator_status,
          earnings
        `)
        .ilike('username', search ? `%${search}%` : '%')
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setUsers(profiles || []);
      setTotalPages(Math.ceil((count || 0) / itemsPerPage));
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase.rpc('soft_delete_user', {
        target_user_id: userId,
        admin_user_id: user.id
      });

      if (error) throw error;

      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, search]);

  return {
    users,
    loading,
    totalPages,
    handleDeleteUser
  };
};

